"use client";

import type { ReactNode } from "react";
import { BasemapSelector } from "@/features/basemaps/components/BasemapSelector";
import { useIsMobile } from "@/features/layout/useIsMobile";
import { useMobileView } from "@/features/layout/useMobileView";
import {
  MapControlBottomCenter,
  MapControlBottomLeft,
  MapControlTop,
  MapControls,
} from "@/components/Map/MapControls";
import { POIDetails } from "@/features/items/components/details/POIDetails";
import { useTrackSelectedItem } from "@/features/analytics/useTrackSelectedItem";
import { MapScale } from "@/features/map/components/MapScale";
import { MediaModals } from "@/features/media/components/MediaModals";
import { AppInfoModal } from "@/features/onboarding/components/AppInfoModal";
import { Tutorial } from "@/features/onboarding/components/Tutorial";
import { useOnboarding } from "@/features/onboarding/useOnboarding";
import { Timeline } from "@/features/timeline/components/Timeline";
import { ShareModal } from "@/features/share/components/ShareModal";
import { Headline } from "@/components/Sidebar/Headline";
import { Sidebar } from "@/components/Sidebar/Sidebar";
import { SidebarToggle } from "@/components/Sidebar/SidebarToggle";
import type { AppLocale } from "@/i18n/routing";
import { MobileListView } from "./MobileListView";
import { MobileMenu } from "./MobileMenu";
import { MobileViewToggle } from "./MobileViewToggle";
import { SearchAndFilters } from "./SearchAndFilters";
import { ToolBar } from "./ToolBar";

/**
 * The app shell over the map.
 *
 * One tree for both breakpoints, rather than the legacy pair of Desktop and Mobile
 * components. Those duplicated most of their contents and had drifted apart — the
 * mobile search had grown near-copies of the filter components. Where the two
 * genuinely differ, that difference is expressed in the component it applies to.
 *
 * The two layouts differ in exactly one structural way, and it's a real one: on
 * desktop the results sit *beside* the map in a collapsible panel, while on mobile
 * they're a separate full-screen view the user switches to. Everything else — the
 * map, the basemap selector, the controls — is shared.
 */
export function AppLayout({
  locales,
  children,
}: {
  locales: AppLocale[];
  children?: ReactNode;
}) {
  const isMobile = useIsMobile();

  // Decides, once, whether this visitor is shown the project's introduction or
  // the first-run tutorial.
  useOnboarding();
  useTrackSelectedItem();

  return (
    <>
      {isMobile ? (
        <MobileLayout locales={locales}>{children}</MobileLayout>
      ) : (
        <DesktopLayout locales={locales}>{children}</DesktopLayout>
      )}

      {/* Outside the breakpoint branches: a modal covers the viewport, so it's
          the same on both, and rendering it in each would remount it whenever the
          window crossed the breakpoint. */}
      <ShareModal />
      <MediaModals />
      <AppInfoModal />
      <Tutorial />
    </>
  );
}

function DesktopLayout({
  locales,
  children,
}: {
  locales: AppLocale[];
  children?: ReactNode;
}) {
  return (
    <div className="pointer-events-none absolute inset-0 flex">
      <Sidebar>{children}</Sidebar>

      <MapControls
        className="flex-auto"
        top={
          <MapControlTop>
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 shrink items-center gap-2">
                <SidebarToggle />
                <SearchAndFilters />
              </div>
              <ToolBar locales={locales} />
            </div>
          </MapControlTop>
        }
        bottomLeft={
          <MapControlBottomLeft>
            <BasemapSelector />
          </MapControlBottomLeft>
        }
        bottomCenter={
          <MapControlBottomCenter>
            <Timeline />
            <MapScale />
          </MapControlBottomCenter>
        }
      />
    </div>
  );
}

function MobileLayout({
  locales,
  children,
}: {
  locales: AppLocale[];
  children?: ReactNode;
}) {
  const { view, isPOIView } = useMobileView();
  const showList = view === "list" && !isPOIView;

  return (
    <>
      {/* The full-width top bar; on mobile it stands in for the desktop `ToolBar`. */}
      <MobileMenu locales={locales} />

      <div className="pointer-events-none absolute inset-0 flex">
        <MapControls
          className="flex-auto"
          bottomLeft={
            // Only over the map: on the list view it would float above the
            // results, changing something the user can't currently see.
            !showList && (
              <MapControlBottomLeft>
                <BasemapSelector />
              </MapControlBottomLeft>
            )
          }
          bottomCenter={
            !showList && (
              <MapControlBottomCenter>
                <MapScale />
              </MapControlBottomCenter>
            )
          }
        />
      </div>

      <MobileListView visible={showList}>
        <Headline />
        {children}
      </MobileListView>
      <MobileViewToggle />

      {/* A whole screen rather than a panel: on a phone the details cover the
          map and the list they were opened from. */}
      {isPOIView && <POIDetails className="fixed inset-0 z-40" />}
    </>
  );
}
